import React, { useCallback } from 'react';
import { BarChart2, Calculator, Database, Server } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент сайдбара для быстрой оценки стоимости проекта
 */
const QuickEstimatorSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  projectTypes,
  cloudProviders
}) => {
  // Расчет текущего объема данных в терабайтах для слайдера
  let dataVolumeInTB = parseFloat(formData.dataVolume) || 0;
  if (formData.dataVolumeUnit === 'gb') {
    dataVolumeInTB = dataVolumeInTB / 1024;
  } else if (formData.dataVolumeUnit === 'pb') {
    dataVolumeInTB = dataVolumeInTB * 1024;
  }
  
  // Расчет текущего срока проекта
  const projectDuration = parseInt(formData.projectDuration) || 0;
  
  // Обработчик изменения объема данных
  const handleDataVolumeChange = (e) => {
    const newValueInTB = parseFloat(e.target.value);
    
    // Конвертируем обратно в исходную единицу измерения
    let newValue;
    if (formData.dataVolumeUnit === 'gb') {
      newValue = (newValueInTB * 1024).toFixed(0);
    } else if (formData.dataVolumeUnit === 'pb') {
      newValue = (newValueInTB / 1024).toFixed(4);
    } else {
      newValue = newValueInTB.toFixed(2);
    }
    
    setFormData(prev => ({
      ...prev,
      dataVolume: newValue
    }));
  };
  
  // Обработчик изменения срока проекта
  const handleProjectDurationChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      projectDuration: newValue
    }));
  };
  
  // Предопределенные объемы данных для быстрого выбора
  const predefinedDataVolumes = [
    { value: 0.1, label: '100 GB' },
    { value: 1, label: '1 TB' },
    { value: 10, label: '10 TB' },
    { value: 100, label: '100 TB' }
  ];
  
  // Предопределенные сроки проекта для быстрого выбора
  const predefinedDurations = [
    { value: '3', label: '3 мес' },
    { value: '6', label: '6 мес' },
    { value: '12', label: '1 год' },
    { value: '24', label: '2 года' }
  ];
  
  // Функция для быстрого выбора объема данных и установки правильной единицы измерения
  const setVolumeWithUnit = (volumeInTB) => {
    let newVolume;
    let newUnit;
    
    if (volumeInTB < 0.1) {
      // Меньше 100 ГБ, используем ГБ
      newVolume = (volumeInTB * 1024).toFixed(0);
      newUnit = 'gb';
    } else if (volumeInTB >= 1024) {
      // Больше или равно 1 ПБ, используем ПБ
      newVolume = (volumeInTB / 1024).toFixed(2);
      newUnit = 'pb';
    } else {
      // От 100 ГБ до 1 ПБ, используем ТБ
      newVolume = volumeInTB.toFixed(2);
      newUnit = 'tb';
    }
    
    setFormData(prev => ({
      ...prev,
      dataVolume: newVolume,
      dataVolumeUnit: newUnit
    }));
  };
  
  // Функция расчета
  const calculateBigData = useCallback(() => {
    // Вызываем функцию основного расчета, которая находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  }, []);
  
  // Быстрая оценка стоимости (приблизительная)
  const getQuickEstimate = () => {
    try {
      // Очень приблизительный расчет для отображения в сайдбаре
      const volumeInTB = dataVolumeInTB;
      const duration = projectDuration;
      const teamSize = parseInt(formData.teamSize) || 5;
      
      // Базовая стоимость хранения данных ($/TB/месяц)
      const storageRate = 20;
      
      // Базовая стоимость специалиста ($/месяц)
      const specialistRate = 7000;
      
      // Множитель для типа проекта
      let projectMultiplier = 1.0;
      switch (formData.projectType) {
        case 'analytics': projectMultiplier = 0.8; break;
        case 'ml': projectMultiplier = 1.3; break;
        case 'realtime': projectMultiplier = 1.5; break;
        case 'datawarehouse': projectMultiplier = 0.7; break;
        case 'dataquality': projectMultiplier = 0.9; break;
        case 'dataintegration': projectMultiplier = 1.1; break;
        default: projectMultiplier = 1.0;
      }
      
      // Множитель для облачного провайдера
      let cloudMultiplier = 1.0;
      switch (formData.cloudProvider) {
        case 'aws': cloudMultiplier = 1.0; break;
        case 'azure': cloudMultiplier = 1.05; break;
        case 'gcp': cloudMultiplier = 0.95; break;
        case 'onpremise': cloudMultiplier = 1.4; break;
        default: cloudMultiplier = 1.0;
      }
      
      // Расчет стоимости хранения
      const storageCost = volumeInTB * storageRate * duration * cloudMultiplier;
      
      // Расчет стоимости персонала
      const personnelCost = teamSize * specialistRate * duration;
      
      // Расчет прочих расходов (обычно 20-30% от суммы хранения и персонала)
      const otherCosts = (storageCost + personnelCost) * 0.25;
      
      // Общая стоимость проекта с учетом типа проекта
      const totalEstimate = (storageCost + personnelCost + otherCosts) * projectMultiplier;
      
      // Ежемесячная стоимость
      const monthlyEstimate = totalEstimate / duration;
      
      return {
        total: totalEstimate,
        monthly: monthlyEstimate
      };
    } catch (error) {
      console.warn("Ошибка быстрой оценки:", error);
      return { total: 0, monthly: 0 };
    }
  };
  
  // Получаем быструю оценку
  const quickEstimate = getQuickEstimate();
  
  // Определяем символ валюты
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode.toLowerCase()) {
      case 'usd': return '$';
      case 'eur': return '€';
      case 'rub': return '₽';
      case 'gbp': return '£';
      default: return currencyCode.toUpperCase();
    }
  };
  
  const currencySymbol = getCurrencySymbol(formData.currency);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая оценка проекта
      </h3>
      
      <div className="space-y-5">
        {/* Объем данных */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="dataVolumeSlider" className="text-gray-600 dark:text-gray-400">Объем данных</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {dataVolumeInTB < 1 
                ? `${(dataVolumeInTB * 1024).toFixed(0)} GB` 
                : dataVolumeInTB >= 1024 
                  ? `${(dataVolumeInTB / 1024).toFixed(2)} PB` 
                  : `${dataVolumeInTB.toFixed(1)} TB`}
            </span>
          </div>
          <input
            type="range"
            id="dataVolumeSlider"
            min="0.1"
            max="1000"
            step="0.1"
            value={dataVolumeInTB}
            onChange={handleDataVolumeChange}
            aria-label="Объем данных"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными объемами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedDataVolumes.map(volume => (
              <button
                key={volume.value}
                type="button"
                onClick={() => setVolumeWithUnit(volume.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${Math.abs(dataVolumeInTB - volume.value) < 0.01
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {volume.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок проекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="projectDurationSlider" className="text-gray-600 dark:text-gray-400">Срок проекта</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {projectDuration <= 1 
                ? '1 месяц' 
                : projectDuration < 12 
                  ? `${projectDuration} мес.` 
                  : `${Math.floor(projectDuration / 12)} ${projectDuration % 12 ? '.' + (projectDuration % 12) : ''} ${pluralizeYears(Math.floor(projectDuration / 12))}`
              }
            </span>
          </div>
          <input
            type="range"
            id="projectDurationSlider"
            min="1"
            max="36"
            step="1"
            value={projectDuration}
            onChange={handleProjectDurationChange}
            aria-label="Срок проекта (месяцев)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedDurations.map(duration => (
              <button
                key={duration.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, projectDuration: duration.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.projectDuration === duration.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {duration.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип проекта */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Тип проекта</div>
          <div className="grid grid-cols-2 gap-2">
            {projectTypes.slice(0, 4).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  projectType: type.id,
                  teamSize: type.defaultTeamSize.toString()
                }))}
                className={`px-2 py-1.5 text-xs rounded border transition-colors
                  ${formData.projectType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Облачный провайдер */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Облачный провайдер</div>
          <div className="grid grid-cols-2 gap-2">
            {cloudProviders.map(provider => (
              <button
                key={provider.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, cloudProvider: provider.id }))}
                className={`px-2 py-1.5 text-xs rounded border transition-colors
                  ${formData.cloudProvider === provider.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {provider.id === 'aws' ? 'AWS' : 
                 provider.id === 'azure' ? 'Azure' : 
                 provider.id === 'gcp' ? 'GCP' : 
                 'On-premise'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Предварительная оценка */}
        <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 text-center">
          <div className="text-sm text-indigo-700 dark:text-indigo-300 mb-1">Примерная оценка стоимости:</div>
          <div className="text-xl font-bold text-indigo-900 dark:text-indigo-100">
            {currencySymbol}{formatNumber(quickEstimate.total, 0)}
          </div>
          <div className="text-xs text-indigo-600 dark:text-indigo-400">
            {currencySymbol}{formatNumber(quickEstimate.monthly, 0)} / месяц
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateBigData}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Детальный расчет
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "год"
const pluralizeYears = (years) => {
  if (years === 0) return 'лет';
  if (years === 1) return 'год';
  if (years >= 2 && years <= 4) return 'года';
  return 'лет';
};

// Функция форматирования чисел
const formatNumber = (value, fractionDigits = 0) => {
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(value);
};

export default QuickEstimatorSidebar;